// kitchen/web/src/components/TableCard.tsx
// Per-table card for the "per-table" KDS view.
// Feature 097: raggruppa per ONDATA (primario, demarcata) → categoria (sotto-sezione).

import { useState, useRef, useEffect, useCallback } from "react";
import { Eye, EyeOff, ChevronUp, ChevronDown } from "lucide-react";
import {
  bucketItemsByWave,
  waveLabelForIndex,
  formatElapsed,
  isActiveKitchenItem,
  itemCategoryId,
  delayTier as computeDelayTier,
} from "@shared/kds/table-board";
import type { TableGroup, KdsBoardConfig, DelayTier } from "@shared/kds/table-board";
import TableDishRow from "./TableDishRow";
import {
  useKitchenStore,
  WAVE_CHANGE_BADGE_MS,
} from "../stores/kitchen.store";

// ─── Props ────────────────────────────────────────────────────────────────────

interface TableCardProps {
  group: TableGroup;
  config: KdsBoardConfig;
  /**
   * The id of the globally-oldest active item across all visible tables.
   * Used to render the FR-015 "più vecchio" badge on that single item.
   */
  oldestItemId?: string | null;
  /**
   * Current timestamp (ms since epoch) passed from TableBoard's ticking state.
   * Used to compute delayTier per item without each row needing its own timer.
   */
  now: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TableCard({
  group,
  config,
  oldestItemId = null,
  now,
}: TableCardProps) {
  // US2 (FR-010): per-card ephemeral toggle, default hidden.
  // Lives in local state — not persisted, resets on unmount/remount.
  const [showServed, setShowServed] = useState(false);

  // Feature 109 (FR-006a): badge temporaneo "spostato in Nª ondata" quando
  // un ordine di questo tavolo ha appena ricevuto un move-wave dal cameriere.
  // Freschezza valutata col tick `now` (nessun timer dedicato); nessun suono.
  const recentWaveChanges = useKitchenStore((s) => s.recentWaveChanges);
  const waveChange = group.orders
    .map((o: any) => recentWaveChanges[o?.id])
    .filter(
      (e): e is { targetWave: number; at: number } =>
        e != null && now - e.at < WAVE_CHANGE_BADGE_MS,
    )
    .sort((a, b) => b.at - a.at)[0];

  // FR-011: the card disappears when there are no active KITCHEN items
  // (items whose category is not mapped to a bucket don't count).
  const hasActiveItems = group.items.some((i) => isActiveKitchenItem(i, config));

  if (!hasActiveItems) return null;

  // Items for bucketing: only mapped active kitchen items.
  const activeItems = group.items.filter((i) => isActiveKitchenItem(i, config));
  // Feature 097: raggruppamento PRIMARIO per ondata; categoria come sotto-bucket.
  const { entree, waves } = bucketItemsByWave(activeItems, config);

  // SERVED items preview — also only items whose category is mapped.
  const servedItems = group.items.filter(
    (i: any) =>
      i.status === "SERVED" && config.categoryToBucket[itemCategoryId(i) ?? ""] != null,
  );
  const activeCount = activeItems.length;
  const servedCount = servedItems.length;

  // Frecce verticali ▲▼: compaiono quando il contenuto della card eccede
  // l'altezza visibile. Scroll programmatico per comande lunghe (touch cucina).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canUp, setCanUp] = useState(false);
  const [canDown, setCanDown] = useState(false);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Se c'è overflow verticale mostra SEMPRE entrambe le frecce (coerente con
    // le orizzontali) così lo chef può sempre scorrere su/giù la comanda.
    const hasOverflow = el.scrollHeight > el.clientHeight + 4;
    setCanUp(hasOverflow);
    setCanDown(hasOverflow);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScroll();
    el.addEventListener("scroll", updateScroll, { passive: true });
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScroll);
      ro.disconnect();
    };
  }, [updateScroll, activeCount, showServed]);

  const scrollBy = (delta: number) =>
    scrollRef.current?.scrollBy({ top: delta, behavior: "smooth" });

  return (
    <div
      className="bg-kds-surface rounded-xl border border-kds-muted/20 flex flex-col h-full max-h-full overflow-hidden"
      style={{ minWidth: "360px", maxWidth: "440px" }}
    >
      {/* Header — table name + active count + show-served toggle */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-kds-muted/20 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          {group.isTakeaway && (
            <span className="text-kds-muted text-sm" title="Asporto / Banco">
              🛍
            </span>
          )}
          <h2 className="text-2xl font-extrabold text-kds-text leading-tight">
            {group.tableName}
          </h2>
          {/* Feature 109 — badge temporaneo spostamento ondate (FR-006a) */}
          {waveChange && (
            <span
              className="text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/40 animate-pulse"
              title="Il cameriere ha spostato piatti su un'altra ondata"
            >
              ↔ spostato in {waveChange.targetWave}ª ondata
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {/* Active item count badge */}
          <span className="text-base font-bold text-kds-muted bg-kds-card px-2 py-0.5 rounded-full">
            {activeCount}
          </span>

          {/* US2 show-served toggle — only rendered when there are served items */}
          {servedCount > 0 && (
            <button
              type="button"
              onClick={() => setShowServed((v) => !v)}
              className={[
                "flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border transition-colors",
                showServed
                  ? "border-kds-muted/60 bg-kds-card text-kds-text"
                  : "border-kds-muted/30 text-kds-muted hover:text-kds-text hover:border-kds-muted/50",
              ].join(" ")}
              title={showServed ? "Nascondi serviti" : `Mostra ${servedCount} servit${servedCount === 1 ? "o" : "i"}`}
            >
              {showServed ? (
                <>
                  <EyeOff size={10} />
                  Nascondi
                </>
              ) : (
                <>
                  <Eye size={10} />
                  +{servedCount}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Scrollable body with vertical ▲▼ arrows for long orders */}
      <div className="relative flex-1 min-h-0">
        {canUp && (
          <button
            type="button"
            aria-label="Scorri su"
            onClick={() => scrollBy(-220)}
            className="absolute top-1 left-1/2 -translate-x-1/2 z-10 flex items-center justify-center w-24 h-12 rounded-xl bg-red-600 text-white border-2 border-red-300 shadow-xl animate-pulse active:scale-95"
          >
            <ChevronUp size={34} strokeWidth={3} />
          </button>
        )}
        <div ref={scrollRef} className="h-full overflow-y-auto p-3 space-y-3">
        {waves.length === 0 && !entree ? (
          // All items SERVED but hasActiveItems guard passed — shouldn't happen.
          <p className="text-kds-muted text-xs text-center py-3">
            Nessun piatto da preparare
          </p>
        ) : (
          <>
            {/* Blocco ENTRÉ — dedicato, SOPRA le ondate (FR-014). */}
            {entree && (
              <div className="rounded-xl border border-kds-muted/30 bg-kds-card/30 overflow-hidden">
                <div className="flex items-center gap-2 px-2.5 py-1.5 bg-kds-muted/15 border-l-4 border-kds-muted">
                  <span className="text-sm font-black uppercase tracking-wider text-kds-text">
                    Entré
                  </span>
                </div>
                <div className="space-y-2 p-2">
                  {entree.items.map((item: any) => {
                    const tier: DelayTier = computeDelayTier(item, now, config);
                    const isOldest = item.id === oldestItemId;
                    return (
                      <TableDishRow
                        key={item.id}
                        item={item}
                        delayTier={tier}
                        isOldest={isOldest}
                        now={now}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Blocchi ONDATA — livello PRIMARIO, demarcazione forte (FR-001/003/004). */}
            {waves.map((wave) => (
              <div
                key={wave.wave}
                className="rounded-xl border-2 border-kds-priority/40 bg-kds-card/30 overflow-hidden"
              >
                {/* Header ondata: barra forte con numero progressivo + cronometro (FR-007). */}
                <div className="flex items-center justify-between gap-2 px-2.5 py-2 bg-kds-priority/20 border-l-4 border-kds-priority">
                  <span className="text-base font-black uppercase tracking-wider text-kds-text leading-none">
                    {waveLabelForIndex(wave.positionIndex)}
                  </span>
                  {wave.oldestTs !== null ? (
                    <span
                      className="text-base font-bold tabular-nums text-kds-text leading-none"
                      title="Tempo dal piatto firato più vecchio dell'ondata"
                    >
                      ⏱ {formatElapsed(now - wave.oldestTs)}
                    </span>
                  ) : (
                    <span
                      className="text-sm font-semibold text-kds-muted italic leading-none"
                      title="Ondata non ancora inviata in cucina: il cronometro parte alla chiamata"
                    >
                      ⏳ In attesa
                    </span>
                  )}
                </div>

                {/* Sotto-sezioni categoria — gerarchia LEGGERA, subordinata all'ondata (FR-006). */}
                <div className="p-2 space-y-2.5">
                  {wave.courseBuckets.map(({ bucket, items }) => (
                    <div key={bucket.id}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-widest text-kds-muted font-semibold">
                          {bucket.name}
                        </span>
                        <div className="h-px flex-1 bg-kds-muted/20" />
                      </div>
                      <div className="space-y-2">
                        {items.map((item: any) => {
                          const tier: DelayTier = computeDelayTier(item, now, config);
                          const isOldest = item.id === oldestItemId;
                          return (
                            <TableDishRow
                              key={item.id}
                              item={item}
                              delayTier={tier}
                              isOldest={isOldest}
                              now={now}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        {/* US2 FR-010: served items preview section, rendered below active buckets */}
        {showServed && servedItems.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-1 mt-1.5">
              <div className="h-px flex-1 bg-kds-muted/20" />
              <span className="text-[9px] uppercase tracking-widest text-kds-muted font-semibold">
                Serviti
              </span>
              <div className="h-px flex-1 bg-kds-muted/20" />
            </div>
            <div className="space-y-1">
              {servedItems.map((item: any) => (
                <TableDishRow
                  key={item.id}
                  item={item}
                  served
                />
              ))}
            </div>
          </div>
        )}
        </div>
        {canDown && (
          <button
            type="button"
            aria-label="Scorri giù"
            onClick={() => scrollBy(220)}
            className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 flex items-center justify-center w-24 h-12 rounded-xl bg-red-600 text-white border-2 border-red-300 shadow-xl animate-pulse active:scale-95"
          >
            <ChevronDown size={34} strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}
